import { motion } from "framer-motion";
import { Clapperboard } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

interface CinematicToggleProps {
  /** Matches the sibling ThemeToggle so icon color adapts to the surrounding text color. */
  useWhiteText?: boolean;
}

/**
 * CinematicToggle
 * ----------------
 * User-facing setting to enable/disable the cinematic video theme transitions.
 * When off, toggling the theme uses a fast crossfade instead of the 6s video.
 * The preference is persisted to localStorage via ThemeContext.
 */
export default function CinematicToggle({ useWhiteText = true }: CinematicToggleProps) {
  const { cinematicEnabled, toggleCinematicEnabled, isTransitioning, isDark } = useTheme();

  const buttonCls = isDark
    ? "bg-white/5 border-white/20 hover:bg-white/10 hover:border-white/30"
    : "bg-transparent border-transparent hover:bg-black/5";

  const iconColor = cinematicEnabled
    ? isDark
      ? "text-blue-400"
      : "text-amber-500"
    : useWhiteText
      ? "text-white/40"
      : "text-gray-400";

  return (
    <button
      onClick={toggleCinematicEnabled}
      disabled={isTransitioning}
      className={`relative w-12 h-12 flex items-center justify-center rounded-none border transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed ${buttonCls}`}
      aria-label={
        cinematicEnabled
          ? "Disable cinematic theme transitions"
          : "Enable cinematic theme transitions"
      }
      aria-pressed={cinematicEnabled}
      title={cinematicEnabled ? "Cinematic transitions: On" : "Cinematic transitions: Off"}
      data-testid="cinematic-toggle"
    >
      <motion.div
        animate={{ scale: cinematicEnabled ? 1 : 0.85, opacity: cinematicEnabled ? 1 : 0.7 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="relative w-5 h-5 flex items-center justify-center"
      >
        <Clapperboard className={`w-5 h-5 transition-colors duration-300 ${iconColor}`} />
      </motion.div>

      {/* Small dot indicating the feature is currently off */}
      {!cinematicEnabled && (
        <span
          className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-gray-500"
          data-testid="cinematic-off-indicator"
        />
      )}
    </button>
  );
}
